import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  getClubSessions,
  getClubTeams,
  getClubCoaches,
  getClubLocations,
  createSession,
  createSessionSeries,
  updateSession,
  updateSessionSeries,
  deleteSession,
  deleteSessionSeries,
  type SeriesScope,
  type ClubSession,
  type ClubCoach,
  type ClubLocation,
} from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import DateInput from "../../components/DateInput";
import { toDateTimeInputValue } from "../../lib/dateFormat";
import toast from "react-hot-toast";

type Team = { id: string; name: string; age_group?: string | null; sport_type?: string | null; coach_id?: string | null };

const WEEKDAY_LABELS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Lu", long: "Luni" },
  { value: 2, short: "Ma", long: "Marți" },
  { value: 3, short: "Mi", long: "Miercuri" },
  { value: 4, short: "Jo", long: "Joi" },
  { value: 5, short: "Vi", long: "Vineri" },
  { value: 6, short: "Sâ", long: "Sâmbătă" },
  { value: 0, short: "Du", long: "Duminică" },
];

type SessionMode = "one_time" | "recurring";

type SessionForm = {
  mode: SessionMode;
  team_id: string;
  coach_id: string;
  type: "sport" | "educational";
  title: string;
  // one-time fields:
  start_at: string;
  end_at: string;
  // recurring fields:
  rec_start_date: string;
  rec_start_time: string;
  rec_end_time: string;
  weekdays: number[];
  /** How the series ends: after a number of weeks, or on a date. */
  rec_end_mode: "weeks" | "until";
  weeks: number;
  rec_until: string;
  // shared:
  location_id: string;
  location_text: string;
};

const DEFAULT_FORM: SessionForm = {
  mode: "one_time",
  team_id: "",
  coach_id: "",
  type: "sport",
  title: "",
  start_at: "",
  end_at: "",
  rec_start_date: "",
  rec_start_time: "17:00",
  rec_end_time: "18:30",
  weekdays: [],
  rec_end_mode: "weeks",
  weeks: 8,
  rec_until: "",
  location_id: "",
  location_text: "",
};

export default function ClubSessions() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();

  const [sessions, setSessions] = useState<ClubSession[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [locations, setLocations] = useState<ClubLocation[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter bar
  const [filterCoach, setFilterCoach] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  // Past sessions are hidden by default so the page opens on what's
  // actually relevant (today + upcoming). The admin can flip the toggle to
  // see history when they need to spot-check past attendance.
  const [showPast, setShowPast] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<SessionForm>(DEFAULT_FORM);

  // Per-session edit modal — reuses a one-time form shape; recurring edits
  // would explode in scope (need to decide single vs all occurrences), so we
  // edit one session at a time here.
  const [editingSession, setEditingSession] = useState<ClubSession | null>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    type: "sport" as "sport" | "educational",
    team_id: "",
    coach_id: "",
    start_at: "",
    end_at: "",
    location_id: "",
    location_text: "",
    notes: "",
  });
  const [editSaving, setEditSaving] = useState(false);

  // Inline delete confirmation (per session) — we set this to the session id
  // whose card should swap its "Delete" icon for the inline yes/no chip.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Bulk-selection state. `selectedIds` is the source of truth — checkboxes
  // are derived from it. We keep it as a Set for O(1) toggles even on big
  // backlogs.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ coach_id: "", location_id: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeletePending, setBulkDeletePending] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  /**
   * Which occurrences an edit applies to, for a session that is part of a
   * recurring series. "single" keeps the old behaviour; the other two are new,
   * and are the answer to coaches who set up a term of Tuesdays and then found
   * there was no way to move the hour.
   */
  const [editScope, setEditScope] = useState<"single" | SeriesScope>("single");

  const openEditSession = (s: ClubSession) => {
    setEditingSession(s);
    // Default to the whole remaining series when there is one: somebody
    // opening a recurring training almost always means "from now on".
    setEditScope(s.is_recurring || s.recurring_parent_id ? "following" : "single");
    setEditForm({
      title: s.title,
      type: s.type,
      team_id: s.team_id,
      coach_id: s.coach_id || s.coach?.id || "",
      // LOCAL wall time — toISOString().slice(0,16) is UTC, and feeding that
      // to a local-time input shifted every edited session back by the UTC
      // offset (2-3h in Romania) on each save.
      start_at: toDateTimeInputValue(s.start_at),
      end_at: toDateTimeInputValue(s.end_at),
      location_id: s.location_id || s.location?.id || "",
      location_text: s.location_text || "",
      notes: (s as any).notes || "",
    });
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSession) return;
    if (!editForm.title.trim()) {
      toast.error(t("admin.sessionFormIncomplete", "Completează titlul."));
      return;
    }
    // A session may not be MOVED into the past. Editing a session that has
    // already happened is still fine as long as its start time is untouched —
    // this form posts every field, so compare against the stored value rather
    // than just testing "is start_at in the past".
    const newStart = new Date(editForm.start_at);
    if (Number.isNaN(newStart.getTime())) {
      toast.error(t("admin.sessionTimeRequired", "Setează data și ora de început și sfârșit."));
      return;
    }
    const startMoved =
      Math.abs(newStart.getTime() - new Date(editingSession.start_at).getTime()) >= 60_000;
    // Only for a single-session edit. On a series edit the past occurrences are
    // deliberately left where they are by the server, so a start time earlier
    // than now is a normal thing to post.
    if (editScope === "single" && startMoved && newStart.getTime() < Date.now()) {
      toast.error(
        t("admin.sessionCannotBeInPast", "Nu poți muta sesiunea în trecut. Alege o dată și oră viitoare.")
      );
      return;
    }

    setEditSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        type: editForm.type,
        start_at: new Date(editForm.start_at).toISOString(),
        end_at: new Date(editForm.end_at).toISOString(),
        coach_id: editForm.coach_id || undefined,
        location_id: editForm.location_id || (locations.length > 0 ? null : undefined),
        location_text: locations.length === 0 ? (editForm.location_text || null) : undefined,
        notes: editForm.notes || null,
      };

      if (editScope === "single") {
        await updateSession(editingSession.id, payload);
        toast.success(t("admin.sessionUpdated", "Sesiune actualizată"));
      } else {
        const res = await updateSessionSeries(editingSession.id, { ...payload, scope: editScope });
        const parts = [`${res.updated} sesiuni actualizate`];
        if (res.retimed > 0) parts.push(`${res.retimed} mutate la ora nouă`);
        if (res.skipped_past > 0) parts.push(`${res.skipped_past} deja ținute, lăsate la ora lor`);
        toast.success(parts.join(" · "));
      }
      await reloadSessions();
      setEditingSession(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || t("admin.sessionUpdateFailed", "Nu s-a putut actualiza sesiunea"));
    } finally {
      setEditSaving(false);
    }
  };

  /** Cancel a recurring training from this occurrence on, or the whole thing. */
  const handleSeriesDelete = async (scope: SeriesScope) => {
    if (!editingSession) return;
    const question =
      scope === "all"
        ? t(
            "admin.confirmDeleteWholeSeries",
            "Anulezi toată seria? Sesiunile deja ținute rămân, cele viitoare se șterg."
          )
        : t("admin.confirmDeleteFollowing", "Anulezi această sesiune și pe cele de după ea?");
    if (!window.confirm(question)) return;
    setEditSaving(true);
    try {
      const res = await deleteSessionSeries(editingSession.id, scope);
      toast.success(
        res.kept_past > 0
          ? `${res.deleted} sesiuni anulate · ${res.kept_past} deja ținute, păstrate`
          : `${res.deleted} sesiuni anulate.`
      );
      await reloadSessions();
      setEditingSession(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Nu am putut anula seria.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleSingleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success(t("admin.sessionDeleted", "Sesiune ștearsă"));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("admin.sessionDeleteFailed", "Nu s-a putut șterge sesiunea"));
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await deleteSession(id);
        ok++;
      } catch {
        fail++;
      }
    }
    setSessions(prev => prev.filter(s => !selectedIds.has(s.id)));
    clearSelection();
    setBulkDeletePending(false);
    setBulkBusy(false);
    if (fail === 0) toast.success(t("admin.bulkDeleteSuccess", "{{count}} sesiuni șterse", { count: ok }));
    else toast.error(t("admin.bulkDeletePartial", "{{ok}} șterse, {{fail}} eșuate", { ok, fail }));
  };

  const handleBulkEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkEditForm.coach_id && !bulkEditForm.location_id) {
      toast.error(t("admin.bulkEditEmpty", "Alege antrenor și/sau locație"));
      return;
    }
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const payload: any = {};
        if (bulkEditForm.coach_id) payload.coach_id = bulkEditForm.coach_id;
        if (bulkEditForm.location_id) payload.location_id = bulkEditForm.location_id;
        await updateSession(id, payload);
        ok++;
      } catch {
        fail++;
      }
    }
    await reloadSessions();
    clearSelection();
    setShowBulkEdit(false);
    setBulkEditForm({ coach_id: "", location_id: "" });
    setBulkBusy(false);
    if (fail === 0) toast.success(t("admin.bulkEditSuccess", "{{count}} sesiuni actualizate", { count: ok }));
    else toast.error(t("admin.bulkEditPartial", "{{ok}} actualizate, {{fail}} eșuate", { ok, fail }));
  };

  useEffect(() => {
    if (!activeClubId) return;
    (async () => {
      setLoading(true);
      try {
        const [s, ts, cs, ls] = await Promise.all([
          getClubSessions(activeClubId),
          getClubTeams(activeClubId),
          getClubCoaches(activeClubId).catch(() => []),
          getClubLocations(activeClubId).catch(() => []),
        ]);
        setSessions(s);
        setTeams(ts as any);
        setCoaches(cs);
        setLocations(ls);
      } catch (error) {
        console.error("Failed to load sessions:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const reloadSessions = async () => {
    if (!activeClubId) return;
    setSessions(await getClubSessions(activeClubId));
  };

  // Number of past sessions in the *current* filter snapshot (ignoring the
  // showPast flag) so the toggle can display the count.
  const pastCount = useMemo(() => {
    const nowTs = Date.now();
    return sessions.filter((s) => {
      if (filterCoach && (s as any).coach_id !== filterCoach && (s as any).coach?.id !== filterCoach) return false;
      if (filterTeam && s.team?.id !== filterTeam) return false;
      if (filterLocation) {
        const sl = (s as any).location_id || (s as any).location?.id || null;
        if (sl !== filterLocation) return false;
      }
      return new Date(s.end_at).getTime() < nowTs;
    }).length;
  }, [sessions, filterCoach, filterTeam, filterLocation]);

  // Apply the filter bar in-memory (the dataset is small; the backend doesn't
  // expose filters as query params today, so we do it client-side).
  const filteredSessions = useMemo(() => {
    const nowTs = Date.now();
    return sessions.filter((s) => {
      if (filterCoach && (s as any).coach_id !== filterCoach && (s as any).coach?.id !== filterCoach) return false;
      if (filterTeam && s.team?.id !== filterTeam) return false;
      if (filterLocation) {
        const sl = (s as any).location_id || (s as any).location?.id || null;
        if (sl !== filterLocation) return false;
      }
      if (filterFrom) {
        const fromTs = new Date(filterFrom).getTime();
        if (!isNaN(fromTs) && new Date(s.start_at).getTime() < fromTs) return false;
      }
      if (filterTo) {
        const toTs = new Date(filterTo).getTime() + 24 * 60 * 60 * 1000 - 1;
        if (!isNaN(toTs) && new Date(s.start_at).getTime() > toTs) return false;
      }
      // Hide past sessions unless the admin explicitly asked for them.
      // A session counts as past once its end_at is behind us.
      if (!showPast && new Date(s.end_at).getTime() < nowTs) return false;
      return true;
    });
  }, [sessions, filterCoach, filterTeam, filterLocation, filterFrom, filterTo, showPast]);

  const clearFilters = () => {
    setFilterCoach("");
    setFilterTeam("");
    setFilterLocation("");
    setFilterFrom("");
    setFilterTo("");
  };

  // Filter-intersection helpers — keep the dropdowns honest so the admin
  // doesn't see "no results" when crossing two unrelated filters:
  //   pick a coach → team dropdown shrinks to teams that coach is on
  //   pick a team  → coach dropdown shrinks to coaches assigned to it
  const availableTeams = useMemo(() => {
    if (!filterCoach) return teams;
    const coach = coaches.find((c) => c.user.id === filterCoach);
    if (!coach) return [];
    const coachTeamIds = new Set(coach.teams.map((t) => t.id));
    return teams.filter((t) => coachTeamIds.has(t.id));
  }, [teams, coaches, filterCoach]);

  const availableCoaches = useMemo(() => {
    if (!filterTeam) return coaches;
    return coaches.filter((c) => c.teams.some((t) => t.id === filterTeam));
  }, [coaches, filterTeam]);

  // If the active filterTeam no longer appears in the available list (because
  // we just picked a coach who doesn't coach it), clear it. Same for the
  // reverse direction. Stops the dropdown showing a value that's filtered out.
  useEffect(() => {
    if (filterTeam && !availableTeams.some((t) => t.id === filterTeam)) {
      setFilterTeam("");
    }
  }, [filterTeam, availableTeams]);
  useEffect(() => {
    if (filterCoach && !availableCoaches.some((c) => c.user.id === filterCoach)) {
      setFilterCoach("");
    }
  }, [filterCoach, availableCoaches]);

  // Group the filtered list by calendar month, then by Mon-Sun week inside
  // each month, so the rendered timeline reads as:
  //   iunie 2026
  //     Săptămâna 1 (15 iun – 21 iun)  [sessions ...]
  //     Săptămâna 2 (22 iun – 28 iun)  [sessions ...]
  // Weeks are ordinal within their month (1st distinct Monday in the month
  // = "Săptămâna 1") so admins can refer to them in conversation.
  const groupedSessions = useMemo(() => {
    const MONTH_LABELS_RO = [
      "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
      "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie",
    ];
    const SHORT_MONTH_RO = [
      "ian", "feb", "mar", "apr", "mai", "iun",
      "iul", "aug", "sep", "oct", "noi", "dec",
    ];
    // Earliest first so the timeline reads naturally; backend returns desc.
    const sorted = [...filteredSessions].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());

    const months: {
      key: string;
      label: string;
      weeks: {
        key: string;
        weekOrdinal: number;
        range: string;
        sessions: ClubSession[];
      }[];
    }[] = [];
    // Track ordinal counter per month (resets on month change).
    const weekOrdinalByMonth = new Map<string, Map<string, number>>();

    for (const s of sorted) {
      const d = new Date(s.start_at);
      const year = d.getFullYear();
      const month = d.getMonth();
      const monthKey = `${year}-${month}`;
      const monthLabel = `${MONTH_LABELS_RO[month]} ${year}`;

      // Monday of this session's week
      const monday = new Date(d);
      monday.setHours(0, 0, 0, 0);
      const dow = monday.getDay();
      monday.setDate(monday.getDate() - (dow === 0 ? 6 : dow - 1));
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const weekKey = `${monday.getFullYear()}-${monday.getMonth()}-${monday.getDate()}`;

      let monthEntry = months.find((m) => m.key === monthKey);
      if (!monthEntry) {
        monthEntry = { key: monthKey, label: monthLabel, weeks: [] };
        months.push(monthEntry);
        weekOrdinalByMonth.set(monthKey, new Map());
      }
      const ordMap = weekOrdinalByMonth.get(monthKey)!;
      let weekEntry = monthEntry.weeks.find((w) => w.key === weekKey);
      if (!weekEntry) {
        const ordinal = ordMap.size + 1;
        ordMap.set(weekKey, ordinal);
        const range = `${monday.getDate()} ${SHORT_MONTH_RO[monday.getMonth()]} – ${sunday.getDate()} ${SHORT_MONTH_RO[sunday.getMonth()]}`;
        weekEntry = { key: weekKey, weekOrdinal: ordinal, range, sessions: [] };
        monthEntry.weeks.push(weekEntry);
      }
      weekEntry.sessions.push(s);
    }
    return months;
  }, [filteredSessions]);

  const openCreateModal = () => {
    setForm({ ...DEFAULT_FORM });
    setShowCreateModal(true);
  };

  // When picking a team, prefill the coach with the team's head coach
  const onTeamChange = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    setForm((f) => ({ ...f, team_id: teamId, coach_id: team?.coach_id || f.coach_id }));
  };

  const toggleWeekday = (wd: number) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(wd) ? f.weekdays.filter((x) => x !== wd) : [...f.weekdays, wd],
    }));
  };

  /**
   * The inline preview of where the series lands, for both ways of ending it.
   *
   * Mirrors POST /sessions/series: for each selected weekday, find the first
   * occurrence on or after the start date and step a week at a time — stopping
   * after `weeks` steps, or at the end date, whichever the user chose. Counting
   * the occurrences rather than multiplying weekdays by weeks matters for the
   * end-date mode, where two weekdays rarely get the same number of slots.
   */
  const recurringEndPreview = useMemo(() => {
    if (form.mode !== "recurring") return null;
    if (!form.rec_start_date || form.weekdays.length === 0) return null;
    const byWeeks = form.rec_end_mode === "weeks";
    if (byWeeks && !form.weeks) return null;
    if (!byWeeks && !form.rec_until) return null;

    const start = new Date(form.rec_start_date + "T00:00:00");
    if (isNaN(start.getTime())) return null;

    let until: Date | null = null;
    if (!byWeeks) {
      until = new Date(form.rec_until + "T23:59:59");
      if (isNaN(until.getTime()) || until < start) return null;
    }

    const sortedWeekdays = [...form.weekdays].sort((a, b) => a - b);
    const MAX_WEEKS = 52;
    const span = byWeeks
      ? form.weeks
      : Math.min(MAX_WEEKS, Math.floor((until!.getTime() - start.getTime()) / (7 * 86400000)) + 1);

    let latest: Date | null = null;
    let count = 0;
    for (const wd of sortedWeekdays) {
      const first = new Date(start);
      first.setDate(first.getDate() + ((wd - first.getDay() + 7) % 7));
      for (let w = 0; w < span; w++) {
        const occ = new Date(first);
        occ.setDate(occ.getDate() + 7 * w);
        if (until && occ > until) break;
        count++;
        if (!latest || occ > latest) latest = occ;
      }
    }

    return latest && count > 0 ? { date: latest, count } : null;
  }, [form.mode, form.rec_start_date, form.weekdays, form.rec_end_mode, form.weeks, form.rec_until]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.team_id || !form.title.trim()) {
      toast.error(t("admin.sessionFormIncomplete", "Completează echipa și titlul."));
      return;
    }

    setSaving(true);
    try {
      if (form.mode === "one_time") {
        if (!form.start_at || !form.end_at) {
          toast.error(t("admin.sessionTimeRequired", "Setează data și ora de început și sfârșit."));
          setSaving(false);
          return;
        }
        const payload: any = {
          team_id: form.team_id,
          type: form.type,
          title: form.title.trim(),
          start_at: new Date(form.start_at).toISOString(),
          end_at: new Date(form.end_at).toISOString(),
        };
        if (form.coach_id) payload.coach_id = form.coach_id;
        if (form.location_id) payload.location_id = form.location_id;
        if (!form.location_id && form.location_text.trim()) payload.location_text = form.location_text.trim();

        await createSession(payload);
        toast.success(t("admin.sessionCreated", "Sesiune creată."));
      } else {
        // recurring
        if (!form.rec_start_date || !form.rec_start_time || !form.rec_end_time) {
          toast.error(t("admin.sessionRecurringTimesRequired", "Setează data de început și intervalul orar."));
          setSaving(false);
          return;
        }
        if (form.weekdays.length === 0) {
          toast.error(t("admin.sessionRecurringWeekdaysRequired", "Selectează cel puțin o zi a săptămânii."));
          setSaving(false);
          return;
        }
        if (form.rec_end_mode === "until" && !form.rec_until) {
          toast.error(t("admin.sessionRecurringUntilRequired", "Alege data de sfârșit a seriei."));
          setSaving(false);
          return;
        }
        const startIso = new Date(`${form.rec_start_date}T${form.rec_start_time}:00`).toISOString();
        const endIso = new Date(`${form.rec_start_date}T${form.rec_end_time}:00`).toISOString();
        const payload: any = {
          team_id: form.team_id,
          type: form.type,
          title: form.title.trim(),
          start_at: startIso,
          end_at: endIso,
          weekdays: form.weekdays,
          // One or the other, never both: the server rejects an empty pair and
          // sending both would leave it guessing which the user meant.
          ...(form.rec_end_mode === "until" && form.rec_until
            ? { until: new Date(`${form.rec_until}T23:59:59`).toISOString() }
            : { weeks: form.weeks }),
        };
        if (form.coach_id) payload.coach_id = form.coach_id;
        if (form.location_id) payload.location_id = form.location_id;
        if (!form.location_id && form.location_text.trim()) payload.location_text = form.location_text.trim();

        const result = await createSessionSeries(payload);
        toast.success(t("admin.sessionSeriesCreated", "Au fost create {{count}} sesiuni.", { count: result.count }));
      }

      setShowCreateModal(false);
      await reloadSessions();
    } catch (err: any) {
      console.error("Failed to create session:", err);
      toast.error(err?.response?.data?.error || t("admin.sessionCreateError", "Nu s-a putut crea sesiunea."));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title={t("admin.sessions", "Sesiuni")}
          subtitle={`${filteredSessions.length} / ${sessions.length} ${t("common.total", "total")}`}
          icon={<ClubNavIcon id="sessions" className="w-6 h-6" />}
          actions={
          <div className="flex items-center gap-2 flex-wrap">
            {/* Past-sessions toggle. Hidden by default so the page opens on
                today + upcoming; the admin can flip it on when they want to
                audit attendance history. */}
            <button
              onClick={() => setShowPast((v) => !v)}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-xl transition-colors ${
                showPast
                  ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              title={t("admin.togglePastHint", "Afișează/ascunde sesiunile trecute")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {showPast
                ? t("admin.hidePast", "Ascunde sesiunile trecute")
                : t("admin.showPast", "Arată sesiunile trecute")}
              {pastCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${showPast ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-700"}`}>
                  {pastCount}
                </span>
              )}
            </button>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("admin.createSession", "Crează sesiune")}
            </button>
          </div>
          }
        />

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        {/* Filter toolbar */}
        <div className="p-3 border-b border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            <select
              value={filterCoach}
              onChange={(e) => setFilterCoach(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t("admin.allCoaches", "Toți antrenorii")}</option>
              {availableCoaches.map((c) => (
                <option key={c.user.id} value={c.user.id}>{c.user.full_name || c.user.email}</option>
              ))}
            </select>
            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t("admin.allTeams", "Toate echipele")}</option>
              {availableTeams.map((tm) => (
                <option key={tm.id} value={tm.id}>{tm.name}</option>
              ))}
            </select>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t("admin.allLocations", "Toate locațiile")}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <DateInput
              value={filterFrom}
              onChange={(iso) => setFilterFrom(iso)}
              placeholder={t("admin.dateFrom", "De la") as string}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title={t("admin.dateFrom", "De la") as string}
            />
            <div className="flex gap-2">
              <DateInput
                value={filterTo}
                onChange={(iso) => setFilterTo(iso)}
                placeholder={t("admin.dateTo", "Până la") as string}
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title={t("admin.dateTo", "Până la") as string}
              />
              {(filterCoach || filterTeam || filterLocation || filterFrom || filterTo) && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm rounded-lg transition-colors"
                  title={t("admin.clearFilters", "Resetează filtre")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bulk action toolbar — a band inside the card when any row is checked */}
        {selectedIds.size > 0 && (
          <div className="px-3 py-2.5 bg-indigo-50 border-b border-indigo-100 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-indigo-800">
              {t("admin.bulkSelected", "{{count}} selectate", { count: selectedIds.size })}
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowBulkEdit(true)}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
              >
                {t("admin.bulkEditButton", "Editează (antrenor / locație)")}
              </button>
              {bulkDeletePending ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 border border-red-300 rounded-lg">
                  <span className="text-sm text-red-900 font-medium">
                    {t("admin.bulkDeleteConfirm", "Ștergi {{count}} sesiuni?", { count: selectedIds.size })}
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkBusy}
                    className="px-2.5 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-xs font-medium disabled:opacity-50"
                  >
                    {bulkBusy ? "..." : t("common.yes", "Da")}
                  </button>
                  <button
                    onClick={() => setBulkDeletePending(false)}
                    disabled={bulkBusy}
                    className="px-2.5 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                  >
                    {t("common.no", "Nu")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setBulkDeletePending(true)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                >
                  {t("admin.bulkDeleteButton", "Șterge selectate")}
                </button>
              )}
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium"
              >
                {t("admin.bulkClear", "Deselectează")}
              </button>
            </div>
          </div>
        )}

        <div className="p-4 space-y-6">
          {groupedSessions.map((month) => (
            <section key={month.key}>
              <h3 className="sticky top-0 z-10 bg-white py-2 -mx-1 px-1 mb-3 text-base sm:text-lg font-bold text-slate-800 capitalize border-b border-slate-200">
                {month.label}
              </h3>
              <div className="space-y-4">
                {month.weeks.map((week) => {
                  const weekIds = week.sessions.map(s => s.id);
                  const allChecked = weekIds.length > 0 && weekIds.every(id => selectedIds.has(id));
                  const someChecked = weekIds.some(id => selectedIds.has(id));
                  return (
                  <div key={week.key}>
                    <div className="flex items-baseline gap-2 mb-2">
                      <label className="flex items-center gap-1.5 cursor-pointer" title={t("admin.selectWeek", "Selectează toată săptămâna") as string}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                          onChange={() => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (allChecked) weekIds.forEach(id => next.delete(id));
                              else weekIds.forEach(id => next.add(id));
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </label>
                      <h4 className="text-sm font-semibold text-indigo-700">
                        {t("admin.weekN", "Săptămâna {{n}}", { n: week.weekOrdinal })}
                      </h4>
                      <span className="text-xs text-slate-500">({week.range})</span>
                      <span className="ml-auto text-xs text-slate-400">{week.sessions.length}</span>
                    </div>
                    <div className="space-y-2">
                      {week.sessions.map((session) => {
                        const isSelected = selectedIds.has(session.id);
                        return (
                        <div
                          key={session.id}
                          className={`p-3 sm:p-4 transition-colors border rounded-xl ${
                            isSelected
                              ? "bg-indigo-50 border-indigo-300"
                              : "bg-slate-50 border-slate-100 hover:border-indigo-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(session.id)}
                              className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-800 break-words">{session.title}</h4>
                              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs sm:text-sm text-slate-500">
                                {session.team?.name && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    {session.team.name}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                  {new Date(session.start_at).toLocaleDateString("ro-RO", { weekday: "short", day: "numeric", month: "short" })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                  {new Date(session.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {" – "}
                                  {new Date(session.end_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {(session.location?.name || session.location_text) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                                    {session.location?.name || session.location_text}
                                  </span>
                                )}
                                {session.coach && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                    {session.coach.full_name || session.coach.email}
                                  </span>
                                )}
                              </div>
                              {/* Per-row inline delete confirmation panel */}
                              {pendingDeleteId === session.id && (
                                <div className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg w-fit">
                                  <span className="text-sm text-red-800">{t("admin.confirmDeleteSession", "Ștergi această sesiune?")}</span>
                                  <button
                                    onClick={() => handleSingleDelete(session.id)}
                                    disabled={deletingId === session.id}
                                    className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium disabled:opacity-50"
                                  >
                                    {deletingId === session.id ? "..." : t("common.yes", "Da")}
                                  </button>
                                  <button
                                    onClick={() => setPendingDeleteId(null)}
                                    disabled={deletingId === session.id}
                                    className="px-2.5 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                                  >
                                    {t("common.no", "Nu")}
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                                {session.type === "sport" ? t("admin.sport", "Sport") : t("admin.educational", "Educațional")}
                              </span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => openEditSession(session)}
                                  title={t("admin.editSession", "Editează sesiunea") as string}
                                  className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button
                                  onClick={() => setPendingDeleteId(session.id)}
                                  title={t("admin.deleteSession", "Șterge sesiunea") as string}
                                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          ))}
          {filteredSessions.length === 0 && (
            <div className="py-12 text-center text-slate-400">
              {sessions.length === 0
                ? t("admin.noSessions", "Nicio sesiune încă")
                : t("admin.noSessionsMatchFilters", "Nicio sesiune nu corespunde filtrelor")}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">{t("admin.createSession", "Crează sesiune")}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form id="create-session-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Step 1: Mode selector */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, mode: "one_time" }))}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    form.mode === "one_time" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t("admin.sessionOneTime", "Sesiune unică")}
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, mode: "recurring" }))}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    form.mode === "recurring" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t("admin.sessionRecurring", "Sesiune recurentă")}
                </button>
              </div>

              {/* Title */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("admin.sessionTitle", "Titlu")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t("admin.sessionTitlePlaceholder", "Ex: Antrenament tehnic")}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("admin.sessionType", "Tip")} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, type: "sport" })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                      form.type === "sport" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t("admin.sport", "Sport")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, type: "educational" })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                      form.type === "educational" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t("admin.educational", "Educațional")}
                  </button>
                </div>
              </div>

              {/* Team + Coach */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("admin.team", "Echipă")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.team_id}
                    onChange={(e) => onTeamChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">{t("admin.selectTeam", "Selectează echipa")}</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                        {team.sport_type ? ` — ${team.sport_type}` : ""}
                        {team.age_group ? ` (${team.age_group})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.coach", "Antrenor")}</label>
                  <select
                    value={form.coach_id}
                    onChange={(e) => setForm({ ...form, coach_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("admin.selectCoach", "Selectează antrenor (opțional)")}</option>
                    {coaches.map((c) => {
                      // When a team is picked, mark every coach that is
                      // already assigned to that team. The head coach gets
                      // "(PRINCIPAL)", any team-scoped role gets "(SECUNDAR)"
                      // so the admin sees at a glance who's already on the
                      // staff without having to remember.
                      const teamEntry = form.team_id
                        ? c.teams.find((t) => t.id === form.team_id)
                        : null;
                      const tag = teamEntry
                        ? teamEntry.is_head
                          ? ` (${t("admin.coachPrincipal", "PRINCIPAL")})`
                          : ` (${t("admin.coachSecundar", "SECUNDAR")})`
                        : "";
                      return (
                        <option key={c.user.id} value={c.user.id}>
                          {(c.user.full_name || c.user.email) + tag}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Time inputs — depends on mode */}
              {form.mode === "one_time" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("admin.sessionStart", "Început")} <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      mode="datetime"
                      value={form.start_at}
                      onChange={(iso) => setForm({ ...form, start_at: iso })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("admin.sessionEnd", "Sfârșit")} <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      mode="datetime"
                      value={form.end_at}
                      onChange={(iso) => setForm({ ...form, end_at: iso })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                  {/* Weekdays */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("admin.recurringWeekdays", "Zilele săptămânii")} <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-7 gap-1.5">
                      {WEEKDAY_LABELS.map((d) => {
                        const active = form.weekdays.includes(d.value);
                        return (
                          <button
                            key={d.value}
                            type="button"
                            onClick={() => toggleWeekday(d.value)}
                            title={d.long}
                            className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                              active ? "bg-indigo-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-indigo-300"
                            }`}
                          >
                            {d.short}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time interval */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-slate-700">
                        {t("admin.timeFrom", "De la")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={form.rec_start_time}
                        onChange={(e) => setForm({ ...form, rec_start_time: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-slate-700">
                        {t("admin.timeTo", "Până la")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={form.rec_end_time}
                        onChange={(e) => setForm({ ...form, rec_end_time: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Start date, then how the series ends */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-slate-700">
                        {t("admin.recurringStartDate", "Data de început")} <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={form.rec_start_date}
                        onChange={(iso) => setForm({ ...form, rec_start_date: iso })}
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      {/* A season ends on a date; a block of training ends
                          after N weeks. Both are normal, so neither is the
                          only option. */}
                      <label className="block mb-2 text-sm font-medium text-slate-700">
                        {t("admin.recurringEndMode", "Seria se termină")} <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-1 p-1 mb-2 rounded-lg bg-slate-100">
                        {([
                          { value: "weeks" as const, label: t("admin.recurringByWeeks", "După săptămâni") },
                          { value: "until" as const, label: t("admin.recurringByDate", "La o dată") },
                        ]).map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setForm({ ...form, rec_end_mode: opt.value })}
                            className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                              form.rec_end_mode === opt.value
                                ? "bg-white text-indigo-700 shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {form.rec_end_mode === "weeks" ? (
                        <input
                          type="number"
                          min={1}
                          max={52}
                          value={form.weeks}
                          onChange={(e) => setForm({ ...form, weeks: Math.max(1, Math.min(52, parseInt(e.target.value || "1", 10))) })}
                          aria-label={t("admin.recurringWeeks", "Săptămâni")}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      ) : (
                        <DateInput
                          value={form.rec_until}
                          onChange={(iso) => setForm({ ...form, rec_until: iso })}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      )}
                    </div>
                  </div>

                  {/* Computed end-date preview */}
                  {recurringEndPreview && (
                    <div className="p-3 bg-white border border-indigo-200 rounded-lg">
                      <p className="text-xs text-indigo-700 font-medium">
                        {t("admin.recurringEndsOn", "Seria se termină pe")}{" "}
                        <span className="font-bold">
                          {recurringEndPreview.date.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {t("admin.recurringTotalSessions", "Total estimat: {{count}} sesiuni", { count: recurringEndPreview.count })}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Location */}
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.location", "Locație")}</label>
                {locations.length > 0 ? (
                  <select
                    value={form.location_id}
                    onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("admin.noLocation", "Fără locație asociată")}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.location_text}
                    onChange={(e) => setForm({ ...form, location_text: e.target.value })}
                    placeholder={t("admin.locationTextPlaceholder", "Ex: Teren Principal")}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>
            </form>

            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm transition-colors"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="submit"
                form="create-session-form"
                disabled={saving}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                {form.mode === "recurring"
                  ? t("admin.createSeriesAction", "Crează seria")
                  : t("admin.createSessionAction", "Crează sesiune")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-session Edit Modal */}
      {editingSession && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">{t("admin.editSessionTitle", "Editează sesiunea")}</h2>
              <button onClick={() => setEditingSession(null)} className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <form id="edit-session-form" onSubmit={handleEditSave} className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Which occurrences, for a recurring session. First in the form
                  because it changes what every field below it means. Editing a
                  series was simply not possible before — the only edit changed
                  one occurrence, so a club whose hall moved an hour had to
                  rebuild the term. */}
              {(editingSession.is_recurring || editingSession.recurring_parent_id) && (
                <div className="p-3 border rounded-xl border-indigo-200 bg-indigo-50/60">
                  <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-indigo-700">
                    {t("admin.seriesScope", "Se aplică la")}
                  </span>
                  <div className="space-y-1.5">
                    {([
                      ["following", t("admin.scopeFollowing", "Această sesiune și cele viitoare"), t("admin.scopeFollowingHint", "Programul s-a schimbat de acum înainte.")],
                      ["single", t("admin.scopeSingle", "Doar această sesiune"), t("admin.scopeSingleHint", "O singură dată mutată; restul seriei rămâne.")],
                      ["all", t("admin.scopeAll", "Toată seria"), t("admin.scopeAllHint", "Inclusiv cele trecute — ora lor nu se schimbă.")],
                    ] as const).map(([value, labelText, hint]) => (
                      <label key={value} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="edit-series-scope"
                          checked={editScope === value}
                          onChange={() => setEditScope(value as "single" | SeriesScope)}
                          className="mt-0.5 w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-800">{labelText}</span>
                          <span className="block text-[11px] text-slate-500">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {editScope !== "single" && (
                    <>
                      <p className="mt-2 text-[11px] text-slate-500">
                        {t(
                          "admin.seriesTimeOnly",
                          "Pentru serie se schimbă doar ora — fiecare sesiune rămâne în ziua ei. Ca să muți una pe altă zi, alege „Doar această sesiune”."
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleSeriesDelete(editScope as SeriesScope)}
                        disabled={editSaving}
                        className="mt-2 text-xs font-bold text-rose-600 hover:text-rose-700 disabled:opacity-50"
                      >
                        {editScope === "all"
                          ? t("admin.cancelWholeSeries", "Anulează toată seria")
                          : t("admin.cancelFollowing", "Anulează de aici înainte")}
                      </button>
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("admin.sessionTitle", "Titlu")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.sessionType", "Tip")}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, type: "sport" })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${editForm.type === "sport" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    {t("admin.sport", "Sport")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, type: "educational" })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${editForm.type === "educational" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    {t("admin.educational", "Educațional")}
                  </button>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.coach", "Antrenor")}</label>
                <select
                  value={editForm.coach_id}
                  onChange={(e) => setEditForm({ ...editForm, coach_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">{t("admin.selectCoach", "Selectează antrenor")}</option>
                  {coaches.map((c) => {
                    // Mirror the create-session dropdown: tag each coach
                    // already assigned to the session's team as PRINCIPAL
                    // (head) or SECUNDAR (team-scoped role), so the admin
                    // sees at a glance who's on this team's staff.
                    const teamEntry = editingSession.team_id
                      ? c.teams.find((tm) => tm.id === editingSession.team_id)
                      : null;
                    const tag = teamEntry
                      ? teamEntry.is_head
                        ? ` (${t("admin.coachPrincipal", "PRINCIPAL")})`
                        : ` (${t("admin.coachSecundar", "SECUNDAR")})`
                      : "";
                    return (
                      <option key={c.user.id} value={c.user.id}>
                        {(c.user.full_name || c.user.email) + tag}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("admin.sessionStart", "Început")} <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    mode="datetime"
                    value={editForm.start_at}
                    min={toDateTimeInputValue(new Date())}
                    onChange={(iso) => setEditForm({ ...editForm, start_at: iso })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("admin.sessionEnd", "Sfârșit")} <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    mode="datetime"
                    value={editForm.end_at}
                    min={editForm.start_at || toDateTimeInputValue(new Date())}
                    onChange={(iso) => setEditForm({ ...editForm, end_at: iso })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.location", "Locație")}</label>
                {locations.length > 0 ? (
                  <select
                    value={editForm.location_id}
                    onChange={(e) => setEditForm({ ...editForm, location_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("admin.noLocation", "Fără locație asociată")}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editForm.location_text}
                    onChange={(e) => setEditForm({ ...editForm, location_text: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.sessionNotes", "Note")}</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </form>

            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditingSession(null)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm transition-colors"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="submit"
                form="edit-session-form"
                disabled={editSaving}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {editSaving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                {t("common.save", "Salvează")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-edit modal: coach and/or location across selected sessions */}
      {showBulkEdit && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">
                {t("admin.bulkEditTitle", "Editează {{count}} sesiuni", { count: selectedIds.size })}
              </h2>
              <button onClick={() => setShowBulkEdit(false)} className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <form id="bulk-edit-form" onSubmit={handleBulkEdit} className="flex-1 overflow-y-auto p-5 space-y-4">
              <p className="text-sm text-slate-500">
                {t("admin.bulkEditHint", "Lasă un câmp gol pentru a-l păstra neschimbat pe sesiunile respective.")}
              </p>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.coach", "Antrenor")}</label>
                <select
                  value={bulkEditForm.coach_id}
                  onChange={(e) => setBulkEditForm({ ...bulkEditForm, coach_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— {t("admin.bulkLeaveUnchanged", "Nu modifica")} —</option>
                  {coaches.map((c) => (
                    <option key={c.user.id} value={c.user.id}>{c.user.full_name || c.user.email}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("admin.location", "Locație")}</label>
                <select
                  value={bulkEditForm.location_id}
                  onChange={(e) => setBulkEditForm({ ...bulkEditForm, location_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={locations.length === 0}
                >
                  <option value="">— {t("admin.bulkLeaveUnchanged", "Nu modifica")} —</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                {locations.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">{t("admin.bulkNoLocations", "Nicio locație definită pentru club.")}</p>
                )}
              </div>
            </form>

            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowBulkEdit(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm transition-colors"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="submit"
                form="bulk-edit-form"
                disabled={bulkBusy || (!bulkEditForm.coach_id && !bulkEditForm.location_id)}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {bulkBusy && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                {t("admin.bulkApply", "Aplică")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
